import { useState, useMemo, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ChevronDown, ChevronRight, Trash2, Star, Building2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminAction } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  AdminPageHeader,
  AdminStatRibbon,
  AdminPanel,
  AdminPageSkeleton,
  AdminSegmented,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

type FilterPreset = "all" | "early";

function statusTone(status?: string): "success" | "info" | "neutral" {
  const s = status?.toLowerCase();
  if (s === "active") return "success";
  if (s === "trialing" || s === "trial") return "info";
  return "neutral";
}

export function AdminOrganizationsTab() {
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [newNote, setNewNote] = useState("");
  const [preset, setPreset] = useState<FilterPreset>("all");
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "organizations"],
    queryFn: () => adminAction("organizations"),
  });

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["admin", "org_members", expandedOrg],
    queryFn: () => adminAction("org_members", { org_id: expandedOrg }),
    enabled: !!expandedOrg,
  });

  const { data: notes, isLoading: notesLoading } = useQuery({
    queryKey: ["admin", "org_notes", expandedOrg],
    queryFn: () => adminAction("org_notes_list", { org_id: expandedOrg }),
    enabled: !!expandedOrg,
  });

  const addNote = useMutation({
    mutationFn: () => adminAction("org_notes_add", { org_id: expandedOrg, note: newNote }),
    onSuccess: () => {
      setNewNote("");
      queryClient.invalidateQueries({ queryKey: ["admin", "org_notes", expandedOrg] });
      toast.success("Note added");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: string) => adminAction("org_notes_delete", { note_id: noteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "org_notes", expandedOrg] });
      toast.success("Note removed");
    },
    onError: () => toast.error("Failed to delete note."),
  });

  const stats = useMemo(() => {
    const orgs = data || [];
    return {
      total: orgs.length,
      active: orgs.filter((o: any) => o.status?.toLowerCase() === "active").length,
      trial: orgs.filter((o: any) => ["trialing", "trial"].includes(o.status?.toLowerCase())).length,
    };
  }, [data]);

  const filtered = useMemo(() => {
    let orgs = data || [];
    const q = search.trim().toLowerCase();
    if (q) {
      orgs = orgs.filter((o: any) =>
        (o.name || "").toLowerCase().includes(q) ||
        (o.slug || "").toLowerCase().includes(q) ||
        (o.plan_type || "").toLowerCase().includes(q) ||
        (o.status || "").toLowerCase().includes(q)
      );
    }
    if (preset === "early") {
      return [...orgs]
        .filter((o: any) => o.status?.toLowerCase() === "active" && o.plan_type && o.plan_type.toLowerCase() !== "free")
        .sort((a: any, b: any) => (a.created_at || "").localeCompare(b.created_at || ""))
        .slice(0, 20);
    }
    return orgs;
  }, [data, preset, search]);

  if (isLoading) return <AdminPageSkeleton stats={3} rows={8} columns={6} />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Customers"
        title="Organisations"
        description="Every workspace on the platform. Drill in for members and support notes."
      />

      <AdminStatRibbon
        items={[
          { label: "Total", value: stats.total, sub: "All organisations" },
          { label: "Active", value: stats.active, sub: "Paying or live" },
          { label: "On trial", value: stats.trial, sub: "In free trial window" },
        ]}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AdminSegmented
          value={preset}
          onChange={(v) => setPreset(v as FilterPreset)}
          options={[
            { value: "all", label: "All" },
            { value: "early", label: "Early customers" },
          ]}
        />
        <div className="flex items-center gap-3">
          {preset === "early" && (
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
              <Star className="w-3 h-3" style={{ color: "#c2541a" }} />
              First {filtered.length} paying, by signup date
            </span>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organisations…"
              className="h-8 w-64 rounded-md border border-border bg-background pl-8 pr-7 text-[12px] outline-none focus:border-[#c2541a]/50 focus:ring-1 focus:ring-[#c2541a]/20"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      <AdminPanel>
        <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
            <tr>
              <th className="w-10" />
              <th className={adminTableHeadCellClass}>Organisation</th>
              <th className={adminTableHeadCellClass}>Plan</th>
              <th className={adminTableHeadCellClass}>Members</th>
              <th className={adminTableHeadCellClass}>Status</th>
              <th className={adminTableHeadCellClass}>Created</th>
              <th className={adminTableHeadCellClass}>Last active</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <Building2 className="w-4 h-4 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-[13px] text-muted-foreground">No organisations match this filter.</p>
                </td>
              </tr>
            ) : filtered.map((org: any, i: number) => (
              <Fragment key={org.id}>
                <tr
                  className={"cursor-pointer " + adminTableRowClass}
                  style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
                  onClick={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
                >
                  <td className="px-4 py-3.5">
                    {expandedOrg === org.id
                      ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  </td>
                  <td className={adminTableCellClass + " text-foreground"} style={{ fontWeight: 500 }}>{org.name}</td>
                  <td className={adminTableCellClass}>
                    <AdminPill tone="neutral">{org.plan_type || "Free"}</AdminPill>
                  </td>
                  <td className={adminTableCellClass + " text-foreground tabular-nums"}>{org.member_count}</td>
                  <td className={adminTableCellClass}>
                    <AdminPill tone={statusTone(org.status)}>{org.status || "—"}</AdminPill>
                  </td>
                  <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                    {new Date(org.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                    {org.last_active ? new Date(org.last_active).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                </tr>
                {expandedOrg === org.id && (
                  <tr>
                    <td colSpan={7} style={{ background: "rgba(58,53,48,0.025)", borderTop: "1px solid rgba(58,53,48,0.06)" }}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-8 py-7">
                        {/* Members column */}
                        <div>
                          <p className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground mb-4">Members</p>
                          {membersLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          ) : (members || []).length === 0 ? (
                            <p className="text-[13px] text-muted-foreground">No members yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {(members || []).map((m: any) => (
                                <div key={m.user_id} className="flex items-center gap-3">
                                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(194,84,26,0.08)" }}>
                                    <span className="text-[11px] font-medium" style={{ color: "#c2541a" }}>
                                      {(m.name || m.email || "?")[0]?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] text-foreground truncate">{m.name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                                  </div>
                                  <AdminPill tone="neutral">{m.role}</AdminPill>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Notes column */}
                        <div>
                          <p className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground mb-4">Support notes</p>
                          <div className="flex gap-2 mb-4">
                            <textarea
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              placeholder="Add internal note…"
                              rows={2}
                              className="flex-1 rounded-[3px] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)] resize-none"
                              style={{ background: "#faf7f1", border: "1px solid rgba(58, 53, 48, 0.14)" }}
                            />
                            <Button size="sm" className="self-end h-8 text-xs" disabled={!newNote.trim() || addNote.isPending} onClick={() => addNote.mutate()}>
                              Add
                            </Button>
                          </div>
                          {notesLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                          ) : (notes || []).length === 0 ? (
                            <p className="text-[12.5px] text-muted-foreground italic">No notes yet.</p>
                          ) : (
                            <div className="space-y-3">
                              {(notes || []).map((n: any) => (
                                <div key={n.id} className="flex items-start gap-3 pl-3" style={{ borderLeft: "1px solid rgba(194,84,26,0.25)" }}>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[12.5px] text-foreground leading-relaxed">{n.note}</p>
                                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground mt-1.5">
                                      {n.author_name} · {new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                    </p>
                                  </div>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => deleteNote.mutate(n.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </AdminPanel>
    </div>
  );
}
